/**
 * MilestoneRow — one launch milestone line: status icon, title, detail, and
 * (per status) one primary action or Retry, plus the payment-provider alternative.
 *
 * The first-party opt-in trap milestone renders its warning sentence
 * emphasized in warning color (testid launch-readiness-optin-trap).
 */

import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { LAUNCH_READINESS_TEST_IDS } from '../testIds';
import {
  FIRST_PARTY_OPT_IN_MILESTONE_ID,
  PAYMENTS_MILESTONE_ID,
  FIRST_PARTY_OPT_IN_WARNING,
} from '../types/launchReadiness.types';
import type { LaunchMilestone } from '../types/launchReadiness.types';
import { milestoneCheckLine, splitDetailAroundWarning } from '../utils/goalMilestones';

export interface MilestoneRowProps {
  milestone: LaunchMilestone;
  /** Navigates to the milestone's nextAction.deepLink (react-router). */
  onAction: (deepLink: string) => void;
  /** Refetches the readiness snapshot (check_unavailable RETRY). */
  onRetry: () => void;
}

/** Status icon per design mock: done=check, missing=!, human_decision=?, check_unavailable=dashed "…". */
function StatusIcon({ status }: { status: LaunchMilestone['status'] }) {
  switch (status) {
    case 'done':
      return <CheckCircleIcon color="success" fontSize="small" />;
    case 'missing':
      return <ErrorIcon color="warning" fontSize="small" />;
    case 'human_decision':
      return <HelpOutlineIcon color="info" fontSize="small" />;
    case 'not_evaluated':
      return <RadioButtonUncheckedIcon color="disabled" fontSize="small" aria-label="Not checked yet" />;
    case 'check_unavailable':
    default:
      return (
        <Box
          component="span"
          aria-label="Check unavailable"
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            border: '2px dashed',
            borderColor: 'divider',
            color: 'text.secondary',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.7rem',
            lineHeight: 1,
            flex: 'none',
          }}
        >
          …
        </Box>
      );
  }
}

/** Detail line; the opt-in trap warning renders emphasized in warning color. */
function MilestoneDetail({ milestone }: { milestone: LaunchMilestone }) {
  const isOptInTrap = milestone.id === FIRST_PARTY_OPT_IN_MILESTONE_ID
    && milestone.status === 'missing'
    && milestone.detail.length > 0;

  if (!isOptInTrap) {
    return (
      <Typography variant="body2" color="text.secondary">
        {milestone.detail}
      </Typography>
    );
  }

  const { before, warning, after } = splitDetailAroundWarning(milestone.detail, FIRST_PARTY_OPT_IN_WARNING);
  return (
    <Typography variant="body2" color="text.secondary">
      {before}
      <Box
        component="strong"
        data-testid={LAUNCH_READINESS_TEST_IDS.optinTrap}
        sx={{ color: 'warning.main', fontWeight: 600 }}
      >
        {warning}
      </Box>
      {after}
    </Typography>
  );
}

export const MilestoneRow: React.FC<MilestoneRowProps> = ({ milestone, onAction, onRetry }) => {
  const checks = milestoneCheckLine(milestone);
  return (
    <Box
      data-testid={LAUNCH_READINESS_TEST_IDS.milestone(milestone.id)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        px: 2.5,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <StatusIcon status={milestone.status} />
      <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
        <Typography variant="body1">{milestone.title}</Typography>
        <MilestoneDetail milestone={milestone} />
        {checks.length > 0 && (
          <Box component="ul" aria-label="Payment checks" sx={{ listStyle: 'none', m: 0, mt: 0.5, p: 0,
            display: 'flex', flexWrap: 'wrap', columnGap: 1.75, rowGap: 0.5 }}>
            {checks.map(check => (
              <Box component="li" key={check.checkId}
                data-testid={LAUNCH_READINESS_TEST_IDS.milestoneCheck(check.checkId)} data-state={check.state}
                aria-label={`${check.label}: ${check.state === 'skipped' ? 'Not checked' : check.state}`}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {check.state === 'passed' && <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />}
                {check.state === 'failed' && <ErrorIcon color="warning" sx={{ fontSize: 16 }} />}
                {check.state === 'skipped' && <RemoveCircleOutlineIcon color="disabled" sx={{ fontSize: 16 }} />}
                <Typography variant="caption" color="text.secondary">{check.label}{check.state === 'skipped' ? ' — Not checked' : ''}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {milestone.status === 'missing' && milestone.nextAction && (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', maxWidth: '100%' }}>
          <Button data-testid={LAUNCH_READINESS_TEST_IDS.milestoneAction} variant="outlined" size="small"
            onClick={() => onAction(milestone.nextAction!.deepLink)}>
            {milestone.nextAction.label}
          </Button>
          {milestone.id === PAYMENTS_MILESTONE_ID && milestone.secondaryAction && (
            <Button data-testid={LAUNCH_READINESS_TEST_IDS.milestoneSecondaryAction(milestone.id)} variant="text" size="small"
              onClick={() => onAction(milestone.secondaryAction!.deepLink)}>
              {milestone.secondaryAction.label}
            </Button>
          )}
        </Box>
      )}
      {milestone.status === 'check_unavailable' && (
        <Button
          data-testid={LAUNCH_READINESS_TEST_IDS.retry}
          variant="outlined"
          size="small"
          sx={{ whiteSpace: 'nowrap', flex: 'none' }}
          onClick={onRetry}
        >
          Retry
        </Button>
      )}
      {milestone.status === 'not_evaluated' && (
        <Typography variant="caption" color="text.secondary">Not checked yet</Typography>
      )}
    </Box>
  );
};

export default MilestoneRow;
